/**
 * Smart Shopping Import
 * Import products from Amazon URLs (affiliate compliant)
 * Real product data extraction
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Link
} from '@mui/material';
import {
  Link as LinkIcon,
  CheckCircle,
  Info
} from '@mui/icons-material';
import { extractAmazonProduct, AMAZON_DISCLOSURE } from '../utils/amazonHelper';

export default function SmartShoppingImport({ open, onClose, onImport }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedProduct, setExtractedProduct] = useState(null);
  const [error, setError] = useState(null);

  const handleExtract = async () => {
    if (!url.trim()) {
      setError('Please enter an Amazon URL');
      return;
    }

    setLoading(true);
    setError(null);
    setExtractedProduct(null);

    try {
      const product = await extractAmazonProduct(url);
      setExtractedProduct(product);
    } catch (err) {
      setError(err.message || 'Failed to extract product info');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = () => {
    if (extractedProduct && onImport) {
      onImport({
        name: extractedProduct.name,
        price: extractedProduct.price,
        category: extractedProduct.category,
        amazonUrl: extractedProduct.amazonUrl,
        asin: extractedProduct.asin,
        imageUrl: extractedProduct.imageUrl,
        estimatedPrice: extractedProduct.estimatedPrice
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setUrl('');
    setExtractedProduct(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LinkIcon />
          Import from Amazon
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Disclosure */}
        <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
          <Typography variant="caption">
            {AMAZON_DISCLOSURE}
          </Typography>
        </Alert>

        {/* URL Input */}
        {!extractedProduct && (
          <Box>
            <TextField
              label="Amazon Product URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              placeholder="https://www.amazon.com/dp/..."
              helperText="Paste any Amazon product link"
              margin="normal"
              autoFocus
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              💡 <strong>How it works:</strong>
              <br />
              1. Find a product on Amazon
              <br />
              2. Copy the URL
              <br />
              3. Paste it here
              <br />
              4. We'll add it to your shopping list!
            </Typography>
          </Box>
        )}

        {/* Extracted Product Preview */}
        {extractedProduct && (
          <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <Box display="flex">
              {extractedProduct.imageUrl && (
                <CardMedia
                  component="img"
                  sx={{ width: 120, objectFit: 'contain', p: 1 }}
                  image={extractedProduct.imageUrl}
                  alt={extractedProduct.name}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              
              <CardContent sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {extractedProduct.name}
                </Typography>

                <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                  <Chip
                    label={extractedProduct.category}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`$${extractedProduct.price.toFixed(2)}`}
                    size="small"
                    color="success"
                  />
                  {extractedProduct.estimatedPrice && (
                    <Chip
                      label="Est. Price"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  ASIN: {extractedProduct.asin}
                </Typography>

                <Link
                  href={extractedProduct.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                >
                  View on Amazon →
                </Link>
              </CardContent>
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
              <Alert severity="success" icon={<CheckCircle />}>
                <Typography variant="body2">
                  Product info extracted! Click "Add to Shopping List" below.
                </Typography>
              </Alert>

              {extractedProduct.estimatedPrice && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    <strong>Note:</strong> Price is estimated. Check Amazon for current pricing.
                  </Typography>
                </Alert>
              )}
            </Box>
          </Card>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        
        {!extractedProduct ? (
          <Button
            onClick={handleExtract}
            variant="contained"
            disabled={!url.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
          >
            {loading ? 'Extracting...' : 'Extract Product'}
          </Button>
        ) : (
          <Button
            onClick={handleImport}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Add to Shopping List
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}












